/**
 * DailyDrilldown — fila expandible del tab "Diario" de Finanzas (Fase 4
 * refactor 2026-05-26).
 *
 * Cada fila de la tabla diaria es clickeable. Al expandirse hace fetch a
 * `/v3/finance/daily/orders?seller_id=X&date=YYYY-MM-DD` y muestra las
 * órdenes/packs del día. Packs (= órdenes con order_ids.length > 1) se
 * muestran agrupados en una sola fila con icono y label "Producto A + N más".
 *
 * Cache: cada fila guarda su propio `data` en estado local. Re-abrir no
 * refetchea. Si el período global cambia, el componente padre re-renderiza
 * la tabla con keys nuevas → estado local se descarta automáticamente.
 */

import { useState, useEffect } from 'react';
import { ChevronRight, Loader2, Package } from 'lucide-react';
import { api } from '../../api/client';
import type {
  FinanceDailyRow,
  DrilldownResponse,
  DrilldownOrderRow,
} from './types';

interface FormatHelpers {
  formatMoney: (n: number) => string;
  formatPct: (n: number) => string;
  formatDay: (d: string) => string;
}

/* ------------------------------------------------------------------ */
/*  StatusBadge                                                       */
/* ------------------------------------------------------------------ */

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    paid: {
      label: 'Pagada',
      cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    delivered: {
      label: 'Entregada',
      cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    cancelled: {
      label: 'Cancelada',
      cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    },
    not_delivered: {
      label: 'No entregada',
      cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    },
    mixed: {
      label: 'Mixto',
      cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
  };
  const entry = map[status] ?? {
    label: status || '—',
    cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${entry.cls}`}
    >
      {entry.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  DrilldownRow (una orden o pack)                                   */
/* ------------------------------------------------------------------ */

function DrilldownRow({
  row,
  fmts,
}: {
  row: DrilldownOrderRow;
  fmts: FormatHelpers;
}) {
  // `is_pack` viene true para casi todas las órdenes modernas (ML asigna
  // pack_id incluso a órdenes single). Para detectar packs REALES usamos
  // order_ids.length > 1 (regla de negocio confirmada en spec).
  const isPackReal = row.order_ids.length > 1;
  const firstName = row.product_names[0] ?? '—';
  const extra = row.product_names.length - 1;
  const productLabel =
    extra > 0 ? `${firstName} + ${extra} más` : firstName;
  const label = isPackReal
    ? `Pack ${row.group_id}`
    : `#${row.order_ids[0] ?? row.group_id}`;
  const profitCls =
    row.profit_usd < 0 ? 'text-red-500' : 'text-green-600';
  const marginCls =
    row.margin_pct >= 10
      ? 'text-green-600'
      : row.margin_pct >= 0
      ? 'text-yellow-600'
      : 'text-red-500';

  return (
    <tr className="border-t border-[var(--color-border-light)] hover:bg-[var(--color-bg-tertiary)]">
      <td className="py-1.5 px-2 font-mono text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
        {label}
      </td>
      <td
        className="py-1.5 px-2 text-[var(--color-text)]"
        title={row.product_names.join('\n')}
      >
        <div className="flex items-center gap-1.5">
          {isPackReal && (
            <Package
              size={14}
              className="inline shrink-0 text-[var(--color-primary)]"
            />
          )}
          <span className="truncate max-w-[320px]">{productLabel}</span>
        </div>
      </td>
      <td className="py-1.5 px-2 text-right text-[var(--color-text)]">
        {row.qty}
      </td>
      <td className="py-1.5 px-2 text-right font-mono text-red-500">
        {fmts.formatMoney(row.cost_usd)}
      </td>
      <td className="py-1.5 px-2 text-right font-mono">
        {fmts.formatMoney(row.net_revenue_usd)}
      </td>
      <td
        className={`py-1.5 px-2 text-right font-mono font-bold ${profitCls}`}
      >
        {fmts.formatMoney(row.profit_usd)}
      </td>
      <td className={`py-1.5 px-2 text-right font-medium ${marginCls}`}>
        {fmts.formatPct(row.margin_pct)}
      </td>
      <td className="py-1.5 px-2">
        <StatusBadge status={row.status} />
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ */
/*  DrilldownTable                                                    */
/* ------------------------------------------------------------------ */

function DrilldownTable({
  data,
  fmts,
}: {
  data: DrilldownResponse;
  fmts: FormatHelpers;
}) {
  if (data.rows.length === 0) {
    return (
      <div className="text-center py-6 text-xs text-[var(--color-text-secondary)]">
        Sin órdenes en esta fecha.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-[var(--color-bg-secondary)] rounded-md border border-[var(--color-border-light)]">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
            <th className="text-left px-2 py-1.5 font-medium">#</th>
            <th className="text-left px-2 py-1.5 font-medium">Producto</th>
            <th className="text-right px-2 py-1.5 font-medium">Cant.</th>
            <th className="text-right px-2 py-1.5 font-medium">Costo</th>
            <th className="text-right px-2 py-1.5 font-medium">Venta neta</th>
            <th className="text-right px-2 py-1.5 font-medium">Ganancia</th>
            <th className="text-right px-2 py-1.5 font-medium">Margen</th>
            <th className="text-left px-2 py-1.5 font-medium">Estado</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <DrilldownRow key={r.group_id} row={r} fmts={fmts} />
          ))}
          <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-bg-card)] font-bold">
            <td className="py-2 px-2" colSpan={2}>
              Total ({data.totals.orders_count}{' '}
              {data.totals.orders_count === 1 ? 'orden' : 'órdenes'})
            </td>
            <td className="py-2 px-2 text-right">{data.totals.qty}</td>
            <td className="py-2 px-2 text-right font-mono text-red-500">
              {fmts.formatMoney(data.totals.cost_usd)}
            </td>
            <td className="py-2 px-2 text-right font-mono">
              {fmts.formatMoney(data.totals.net_revenue_usd)}
            </td>
            <td
              className={`py-2 px-2 text-right font-mono ${
                data.totals.profit_usd < 0
                  ? 'text-red-500'
                  : 'text-green-600'
              }`}
            >
              {fmts.formatMoney(data.totals.profit_usd)}
            </td>
            <td className="py-2 px-2 text-right">—</td>
            <td className="py-2 px-2">—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DailyRowWithDrilldown — exportada al index                        */
/* ------------------------------------------------------------------ */

export function DailyRowWithDrilldown({
  row,
  sellerId,
  formatMoney,
  formatPct,
  formatDay,
}: {
  row: FinanceDailyRow;
  sellerId: number;
  formatMoney: (n: number) => string;
  formatPct: (n: number) => string;
  formatDay: (d: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<DrilldownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Solo dispara cuando se abre por primera vez. `loading` y `data` NO van
    // en las dependencias: si los incluyeramos, setLoading(true) re-dispara
    // el effect, la cleanup pone cancelled=true, y el fetch original descarta
    // su resultado → la UI queda colgada en "Cargando…" para siempre.
    if (!open || data) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // row.day puede llegar como timestamp ISO ("2026-05-25T04:00:00.000Z")
    // del backend. El endpoint exige YYYY-MM-DD, así que normalizamos.
    const isoDay = String(row.day).slice(0, 10);
    api
      .get<DrilldownResponse>(
        `/v3/finance/daily/orders?seller_id=${sellerId}&date=${isoDay}`,
      )
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error || 'No se pudo cargar el detalle del día.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Error de red al cargar el detalle.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row.day, sellerId]);

  const fmts: FormatHelpers = { formatMoney, formatPct, formatDay };
  const profitCls =
    row.profit >= 0 ? 'text-green-600' : 'text-red-500';
  const marginCls =
    row.avg_margin >= 10
      ? 'text-green-600'
      : row.avg_margin >= 0
      ? 'text-yellow-600'
      : 'text-red-500';

  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer hover:bg-[var(--color-bg-secondary)]"
      >
        <td className="px-2 py-3 w-8">
          <ChevronRight
            className={`w-4 h-4 text-[var(--color-text-secondary)] transition-transform ${
              open ? 'rotate-90' : ''
            }`}
          />
        </td>
        <td className="px-4 py-3 font-medium">{formatDay(row.day)}</td>
        <td className="px-4 py-3 text-right">{row.orders}</td>
        <td className="px-4 py-3 text-right font-mono">
          {formatMoney(row.revenue)}
        </td>
        <td
          className={`px-4 py-3 text-right font-mono font-bold ${profitCls}`}
        >
          {formatMoney(row.profit)}
        </td>
        <td className={`px-4 py-3 text-right font-medium ${marginCls}`}>
          {formatPct(row.avg_margin)}
        </td>
      </tr>
      {open && (
        <tr className="bg-[var(--color-bg-secondary)]">
          <td />
          <td colSpan={5} className="px-4 py-3">
            {loading && (
              <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Cargando órdenes…
              </div>
            )}
            {!loading && error && (
              <div className="text-xs text-red-500 py-2">{error}</div>
            )}
            {!loading && !error && data && (
              <DrilldownTable data={data} fmts={fmts} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default DailyRowWithDrilldown;
